import { useEffect, useRef, useState } from "react";
import type { CategoryKey } from "../types";
import { CATEGORY_KEYS } from "../types";

export type View = "analyze" | "transcript";
export type SectionKey = "summary" | "action-items";

interface UseMeetingDetailOptions {
	/** Called when J/K keyboard shortcut cycles dimensions. */
	onDimCycle?: (key: CategoryKey) => void;
}

export function useMeetingDetail(
	initialSpeakerId: string,
	initialDim: CategoryKey | null = null,
	_options: UseMeetingDetailOptions = {},
) {
	const [view, setView] = useState<View>("analyze");
	const [dim, setDimRaw] = useState<CategoryKey | null>(initialDim);
	const [section, setSectionRaw] = useState<SectionKey>("summary");
	const [speakerId, setSpeakerId] = useState<string>(initialSpeakerId);
	const [playerOpen, setPlayerOpen] = useState(false);
	const [playhead, setPlayhead] = useState(0);

	// Keep a ref to the current dim for keyboard handler without stale closure
	const dimRef = useRef(dim);
	dimRef.current = dim;
	const viewRef = useRef(view);
	viewRef.current = view;
	const playerOpenRef = useRef(playerOpen);
	playerOpenRef.current = playerOpen;
	const speakerIdRef = useRef(speakerId);
	speakerIdRef.current = speakerId;

	// Speaker list ref for Up/Down navigation
	const speakersRef = useRef<string[]>([initialSpeakerId]);

	// Handler intentionally registers once on mount — it reads refs for live values
	// and calls the stable setter wrappers; re-attaching on every render isn't useful.
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			// Ignore when an input/textarea/contenteditable is focused
			const el = document.activeElement;
			if (!el) return;
			const tag = (el as HTMLElement).tagName;
			if (tag === "INPUT" || tag === "TEXTAREA" || (el as HTMLElement).isContentEditable) {
				return;
			}

			switch (e.key) {
				case "j":
				case "J": {
					const keys = [...CATEGORY_KEYS];
					// When dim is null, idx is -1; (−1 + 1) % 6 = 0 → first key
					const idx = dimRef.current !== null ? keys.indexOf(dimRef.current) : -1;
					const next = keys[(idx + 1) % keys.length];
					if (next) setDimRaw(next);
					break;
				}
				case "k":
				case "K": {
					const keys = [...CATEGORY_KEYS];
					// When dim is null, idx is -1; (−1 − 1 + 6) % 6 = 4 → last key
					const idx = dimRef.current !== null ? keys.indexOf(dimRef.current) : keys.length;
					const prev = keys[(idx - 1 + keys.length) % keys.length];
					if (prev) setDimRaw(prev);
					break;
				}
				case "t":
				case "T":
					setView((v) => (v === "analyze" ? "transcript" : "analyze"));
					break;
				case "r":
				case "R":
					setPlayerOpen((v) => !v);
					break;
				case "ArrowUp": {
					e.preventDefault();
					const speakers = speakersRef.current;
					const idx = speakers.indexOf(speakerIdRef.current);
					const prev = speakers[(idx - 1 + speakers.length) % speakers.length];
					if (prev) setSpeakerId(prev);
					break;
				}
				case "ArrowDown": {
					e.preventDefault();
					const speakers = speakersRef.current;
					const idx = speakers.indexOf(speakerIdRef.current);
					const next = speakers[(idx + 1) % speakers.length];
					if (next) setSpeakerId(next);
					break;
				}
				// Space, Left, Right are handled by the audio element itself when focused;
				// for global shortcuts we only care when no element with audio context is active.
			}
		};

		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, []);

	/** Set the active dimension. Selecting a dimension takes render precedence over the active
	 *  section, but does not change the section value. Pass null to deselect. */
	const setDim = (k: CategoryKey | null) => {
		setDimRaw(k);
	};

	/** Switch active section and clear the active dimension so the section view is visible. */
	const setSection = (s: SectionKey) => {
		setSectionRaw(s);
		setDimRaw(null);
	};

	return {
		view,
		setView,
		dim,
		setDim,
		section,
		setSection,
		speakerId,
		setSpeakerId,
		playerOpen,
		setPlayerOpen,
		playhead,
		setPlayhead,
		/** Register the ordered speaker id list so Up/Down can cycle through them. */
		setSpeakers: (ids: string[]) => {
			speakersRef.current = ids;
		},
	};
}
